import React, { useState, useEffect } from "react";
import DashboardHeader from "../components/DashboardHeader";
import { Container } from "react-bootstrap";
import { CheckCircle2, Clock, CreditCard, Mail, ShieldCheck, Calendar, Sparkles } from "lucide-react";

// Importă imaginile cardurilor (asigură-te că le ai în folderul assets)
import arhitectImg from "../assets/arhitect.png";
import ambasadorImg from "../assets/ambasador.png";
import maestruImg from "../assets/maestru.png";
import vizionarImg from "../assets/vizionar.png";
import membruImg from "../assets/membru.png";

const TOKEN = "unwantedtok";
const API_BASE = "https://prdp-api.suport-949.workers.dev/api";

export default function CotizatiiPage({ memberData }) {
  const [istoriculCotizatiilor, setIstoriculCotizatiilor] = useState([]);
  const [loading, setLoading] = useState(true);

  // Funcție pentru a determina imaginea cardului
  const getCardImage = (pachet) => {
    const p = pachet ? pachet.toLowerCase() : "membru";
    switch (p) {
      case "arhitect": return arhitectImg;
      case "ambasador": return ambasadorImg;
      case "maestru": return maestruImg;
      case "vizionar": return vizionarImg;
      default: return membruImg;
    }
  };

  // Funcție pentru a calcula data de expirare
  const getExpirationDate = (tipCotizatie) => {
    const now = new Date();
    let expDate;
    
    if (tipCotizatie === "anual") {
      // Ultima zi a anului curent
      expDate = new Date(now.getFullYear(), 11, 31);
    } else {
      // Ultima zi a lunii curente
      expDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }
    
    return expDate.toLocaleDateString("ro-RO", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  useEffect(() => {
    async function fetchCotizatii() {
      if (!memberData?.id) {
        setIstoriculCotizatiilor([]);
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(
          `${API_BASE}/cotizatii/membru/${memberData.id}`,
          {
            headers: {
              Authorization: `Bearer ${TOKEN}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) throw new Error("Eroare la preluarea cotizațiilor");
        const data = await response.json();

        const formattedData = data.map((item) => ({
          tip: item.tip_cotizatie || "Cotizație",
          data: item.data_start
            ? new Date(item.data_start).toLocaleDateString("ro-RO", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
            : "Data necunoscută",
          suma: item.suma ?? 0,
          status: item.status === "platit" ? "platit" : "în așteptare",
          tipPlata: item.modalitate_plata ?? "Necunoscut",
        }));
        setIstoriculCotizatiilor(formattedData);
      } catch (error) {
        console.error(error);
        setIstoriculCotizatiilor([]);
      } finally {
        setLoading(false);
      }
    }
    fetchCotizatii();
  }, [memberData]);

  const pachet = memberData?.pachet_cotizatie || "Membru";
  const frecventa = memberData?.tip_cotizatie || "lunar";
  const cardImage = getCardImage(pachet);
  const expirationDate = getExpirationDate(frecventa);
  const statusMembru = memberData?.status_membru || "inactiv";

  return (
    <div className="dashboard-wrapper" style={{ overflowX: "hidden", minHeight: "100vh", backgroundColor: "#040914", paddingBottom: "80px", fontFamily: "Inter, system-ui, sans-serif" }}>
      <DashboardHeader user={memberData} />
      
      <style>{`
        .glass-card {
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 32px;
        }
        .premium-shadow {
          box-shadow: 0 30px 60px rgba(0, 0, 0, 0.4), 0 0 40px rgba(245, 158, 11, 0.05);
        }
        .card-img-wrapper img {
          transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), filter 0.5s ease;
          filter: drop-shadow(0 20px 30px rgba(0,0,0,0.5));
        }
        .card-img-wrapper:hover img {
          transform: translateY(-10px) scale(1.02);
          filter: drop-shadow(0 30px 40px rgba(245, 158, 11, 0.2));
        }
        .action-btn {
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .action-btn:hover {
          transform: scale(1.02);
          box-shadow: 0 10px 25px rgba(245, 158, 11, 0.3);
        }
      `}</style>

      <Container className="pt-5 mt-4">
        
        {/* TITLU PAGINĂ */}
        <div className="text-center mb-5">
          <h1 style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 800, color: "#fff", letterSpacing: "-0.03em", marginBottom: "12px" }}>
            Cotizația ta <span style={{ color: "#fbbf24" }}>PRDP</span>
          </h1>
          <p style={{ color: "#94a3b8", fontSize: "1.1rem", maxWidth: "600px", margin: "0 auto" }}>
            Gestionează-ți cotizația, verifică valabilitatea pachetului și istoricul plăților într-un singur loc.
          </p>
        </div>

        {/* HERO CARD - STATUS CURENT */}
        <div className="glass-card premium-shadow p-4 p-md-5 mb-5 position-relative overflow-hidden">
          {/* Fundal gradient subtil în interiorul cardului */}
          <div style={{ position: "absolute", top: "-50%", left: "-20%", width: "70%", height: "150%", background: "radial-gradient(circle, rgba(245,158,11,0.05) 0%, transparent 60%)", pointerEvents: "none" }}></div>
          
          <div className="row align-items-center position-relative z-1">
            {/* Imaginea Legitimației */}
            <div className="col-lg-6 mb-4 mb-lg-0 text-center card-img-wrapper">
              <img 
                src={cardImage} 
                alt={`Card ${pachet}`} 
                style={{ width: "100%", maxWidth: "450px", height: "auto", objectFit: "contain" }} 
              />
            </div>

            {/* Detalii Pachet */}
            <div className="col-lg-6 ps-lg-5">
              <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: statusMembru === 'activ' ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)", border: `1px solid ${statusMembru === 'activ' ? "rgba(16, 185, 129, 0.2)" : "rgba(239, 68, 68, 0.2)"}`, borderRadius: "999px", padding: "6px 16px", fontSize: "0.8rem", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: statusMembru === 'activ' ? "#10b981" : "#ef4444", marginBottom: "24px" }}>
                {statusMembru === 'activ' ? <ShieldCheck size={16} /> : <Clock size={16} />}
                Statut {statusMembru}
              </div>

              <h2 style={{ fontSize: "2.5rem", fontWeight: 800, color: "#fff", marginBottom: "8px", letterSpacing: "-0.02em" }}>
                Pachet {pachet}
              </h2>
              
              <div style={{ fontSize: "1.1rem", color: "#94a3b8", marginBottom: "32px", display: "flex", alignItems: "center", gap: "8px" }}>
                Cotizație <strong style={{ color: "#fff", textTransform: "capitalize" }}>{frecventa}</strong>
              </div>

              <div className="d-flex flex-column gap-3 mb-4">
                <div style={{ display: "flex", alignItems: "center", gap: "16px", background: "rgba(255,255,255,0.03)", padding: "16px 24px", borderRadius: "20px", border: "1px solid rgba(255,255,255,0.05)" }}>
                  <div style={{ background: "rgba(245,158,11,0.1)", padding: "12px", borderRadius: "14px" }}>
                    <Calendar size={24} color="#fbbf24" />
                  </div>
                  <div>
                    <div style={{ fontSize: "0.85rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Valabilitate</div>
                    <div style={{ fontSize: "1.1rem", color: "#fff", fontWeight: 700 }}>Expiră la {expirationDate}</div>
                  </div>
                </div>
              </div>

              <a href="https://revolut.me/partidulprdp" target="_blank" rel="noreferrer noopener" style={{ textDecoration: "none" }}>
                <button
                  className="action-btn"
                  style={{
                    width: "100%",
                    padding: "16px 24px",
                    borderRadius: "16px",
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    border: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    backgroundColor: "#fbbf24",
                    color: "#0f172a",
                    cursor: "pointer"
                  }}
                >
                  <CreditCard size={20} /> 
                  {statusMembru === 'activ' ? "Plătește următoarea cotizație" : "Activează-ți calitatea de membru"}
                </button>
              </a>
            </div>
          </div>
        </div>

        {/* ISTORIC COTIZAȚII */}
        <div className="mt-5 pt-4">
          <h3 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#fff", display: "flex", alignItems: "center", gap: "10px", marginBottom: "24px" }}>
            <Clock color="#fbbf24" size={24} /> Istoricul plăților
          </h3>

          <div className="glass-card" style={{ padding: "32px" }}>
            {loading ? (
              <div style={{ color: "#94a3b8", textAlign: "center", padding: "40px", fontStyle: "italic", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
                <div className="spinner-border text-warning" role="status"></div>
                Se încarcă istoricul...
              </div>
            ) : istoriculCotizatiilor.length === 0 ? (
              <div style={{ color: "#94a3b8", textAlign: "center", padding: "40px", fontSize: "1.1rem" }}>
                Nu există plăți înregistrate momentan.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {istoriculCotizatiilor.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.2)",
                      borderRadius: "20px",
                      padding: "20px 24px",
                      border: "1px solid rgba(255, 255, 255, 0.04)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "16px",
                      transition: "background 0.3s ease",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.03)"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.2)"}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                      <div style={{ backgroundColor: item.status === "platit" ? "rgba(16, 185, 129, 0.1)" : "rgba(251, 191, 36, 0.1)", padding: "14px", borderRadius: "16px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {item.status === "platit" ? <CheckCircle2 size={24} color="#10b981" /> : <Clock size={24} color="#fbbf24" />}
                      </div>
                      <div>
                        <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "4px", textTransform: "capitalize" }}>
                          {item.tip}
                        </div>
                        <div style={{ color: "#94a3b8", fontSize: "0.9rem", fontWeight: 500 }}>
                          {item.data} • {item.tipPlata}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "24px", textAlign: "right" }}>
                      <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#fff", letterSpacing: "-0.5px" }}>
                        {item.suma} <span style={{ fontSize: "1rem", color: "#94a3b8", fontWeight: 600 }}>RON</span>
                      </div>
                      <div
                        style={{
                          backgroundColor: item.status === "platit" ? "rgba(16, 185, 129, 0.1)" : "rgba(251, 191, 36, 0.1)",
                          color: item.status === "platit" ? "#10b981" : "#fbbf24",
                          border: `1px solid ${item.status === "platit" ? "rgba(16, 185, 129, 0.2)" : "rgba(251, 191, 36, 0.2)"}`,
                          padding: "8px 20px",
                          borderRadius: "999px",
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                          minWidth: "120px",
                          textAlign: "center"
                        }}
                      >
                        {item.status === "platit" ? "Plătit" : "În așteptare"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* CARD CONTACT */}
        <div 
          className="mt-5 glass-card"
          style={{ 
            padding: "48px 32px", 
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            background: "linear-gradient(180deg, rgba(15,23,42,0.4) 0%, rgba(245,158,11,0.05) 100%)",
            border: "1px solid rgba(245,158,11,0.15)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "64px", height: "64px", borderRadius: "50%", background: "rgba(245,158,11,0.1)", color: "#fbbf24", marginBottom: "24px" }}>
            <Sparkles size={32} />
          </div>
          <h3 style={{ fontWeight: 800, fontSize: "1.75rem", color: "#fff", marginBottom: "12px", letterSpacing: "-0.02em" }}>
            Ai nevoie de asistență?
          </h3>
          <p style={{ color: "#94a3b8", fontSize: "1.1rem", marginBottom: "32px", maxWidth: "500px", lineHeight: "1.6" }}>
            Suntem aici să te ajutăm. Contactează departamentul financiar pentru orice clarificări legate de calitatea ta de membru sau plățile efectuate.
          </p>
          <button
            className="action-btn"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              color: "#fff",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "16px",
              padding: "14px 32px",
              fontWeight: 600,
              fontSize: "1rem",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              cursor: "pointer",
            }}
            onClick={() => (window.location.href = "mailto:suport@prdp.ro")}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
          >
            <Mail size={18} /> Trimite un email
          </button>
        </div>

      </Container>
    </div>
  );
}
